import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery } from "redux-saga/effects";

import camelcaseKeys from "camelcase-keys";

import {
  fetchClients,
  fetchClientsSuccess,
  fetchClientsFailure,
  setAgencyLoading,
  addClient,
  addClientSuccess,
  addClientFailure,
  setAddLoading,
  detachClient,
  detachClientSuccess,
  detachClientFailure,
  setDetachLoading,
  actAsClient,
  returnToAgency,
  enableAgency,
  enableAgencySuccess,
  enableAgencyFailure,
  setEnableAgencyLoading,
  inviteClient,
  inviteClientSuccess,
  inviteClientFailure,
  setInviteLoading,
  createClient,
  createClientSuccess,
  createClientFailure,
  setCreateLoading,
  renameClient,
  renameClientSuccess,
  renameClientFailure,
  setRenameLoading,
} from "./slice";
import { setActingAs, clearActingAs, setAgencyInfo } from "../auth/slice";
import { resetImpersonationState } from "../actions";
import { applySagaError } from "../utils/saga";
import { selectAuthIsAgency, selectAuthOwnAccountId } from "../selectors/auth";
import { API_ENDPOINTS } from "../../constants/api";
import { ERoutes } from "../../constants/routes";
import { apiGet, apiPost, apiPatch } from "../../utils/apiClient";
import { fetchClientBrandStats } from "../../api/agencyBrandStats";
import { AddClientPayload, DetachClientPayload, ManagedAccount, ManagedAccountBrand, ActAsPayload, InviteClientPayload, CreateClientPayload, RenameClientPayload } from "./types";

/* ---------- helpers ---------- */

const AGENCY_DASHBOARD_PATH = "/agency/";
const AGENCY_API_OPTIONS = { auth: "tokenApi" as const, skipActingAs: true };

function normalize<T>(data: unknown): T {
  return camelcaseKeys(data as Record<string, unknown>, { deep: true }) as T;
}

function* requireAccountId() {
  const id: string | null = yield select(selectAuthOwnAccountId);
  return id;
}

function* requireAccountIdOrFail() {
  const id: string | null = yield* requireAccountId();
  if (!id) throw new Error("No account");
  return id;
}

function agencyUrl(endpoint: string, userID: string, suffix = "") {
  return `${endpoint}${suffix}?user_id=${encodeURIComponent(userID)}`;
}

/** @deprecated Одноразовый Supabase-клиент — больше не используется (create client перенесён на backend). */

/** Привязать email как managed account агентства. */
function* attachManagedAccount(userID: string, clientEmail: string, label?: string | null) {
  yield call(
    apiPost<ManagedAccount>,
    agencyUrl(API_ENDPOINTS.agencyManagedAccounts, userID),
    {
      ...AGENCY_API_OPTIONS,
      body: {
        client_email: clientEmail,
        label: label ?? null,
        login_url: `${window.location.origin}${ERoutes.Login}`,
      },
    },
  );
}

/** Создаёт agency-аккаунт при первом клиенте, если ещё не включён. */
function* ensureAgencyEnabled() {
  const isAgency: boolean = yield select(selectAuthIsAgency);
  if (isAgency) return;

  const userID: string = yield* requireAccountIdOrFail();

  yield call(
    apiPost,
    agencyUrl(API_ENDPOINTS.agencyCreate, userID),
    AGENCY_API_OPTIONS,
  );

  yield put(setAgencyInfo({ isAgency: true }));
}

/** Load per-brand stats for managed clients missing brand data.
 *
 * Optimization:
 * - If the API already returned brands (Phase 2), skip enrichment entirely.
 * - Otherwise, fetch all missing clients in parallel (Phase 1), not sequentially.
 */
function* enrichClientsWithBrandStats(
  clients: ManagedAccount[],
  agencyUserId: string,
): Generator {
  const alreadyEnriched: ManagedAccount[] = [];
  const needsEnrichment: ManagedAccount[] = [];

  for (const client of clients) {
    if (client.brandCount === 0) {
      alreadyEnriched.push({ ...client, brands: [] });
    } else if (client.brands && client.brands.length > 0) {
      alreadyEnriched.push(client);
    } else {
      needsEnrichment.push(client);
    }
  }

  if (needsEnrichment.length === 0) {
    return alreadyEnriched;
  }

  // Parallel fetch for all clients that need enrichment
  const enrichedResults: ManagedAccount[] = yield call(() =>
    Promise.all(
      needsEnrichment.map((client) =>
        fetchClientBrandStats(
          client.clientAccountId,
          client.clientOwnerId,
          agencyUserId,
        )
          .then((brands) => ({ ...client, brands }))
          .catch(() => ({ ...client, brands: [] as ManagedAccountBrand[] })),
      ),
    ),
  );

  return [...alreadyEnriched, ...enrichedResults];
}

/* ---------- sagas ---------- */

function* fetchClientsSaga() {
  try {
    yield put(setAgencyLoading());
    const userID: string = yield* requireAccountIdOrFail();

    const data: ManagedAccount[] = yield call(
      apiGet<ManagedAccount[]>,
      agencyUrl(API_ENDPOINTS.agencyManagedAccounts, userID),
      AGENCY_API_OPTIONS,
    );

    const normalized = normalize<ManagedAccount[]>(data);

    // If backend already provides per-brand stats, skip enrichment entirely
    const allHaveBrands = normalized.every(
      (c) => c.brandCount === 0 || (c.brands && c.brands.length > 0),
    );

    let clients: ManagedAccount[];
    if (allHaveBrands) {
      clients = normalized.map((c) => ({
        ...c,
        brands: c.brands ?? [],
      }));
    } else {
      clients = yield call(enrichClientsWithBrandStats, normalized, userID);
    }

    yield put(fetchClientsSuccess(clients));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.fetchClients",
      failureAction: fetchClientsFailure,
    });
  }
}

function* addClientSaga(action: PayloadAction<AddClientPayload>) {
  try {
    yield put(setAddLoading());
    const userID: string = yield* requireAccountIdOrFail();

    yield call(ensureAgencyEnabled);
    yield call(attachManagedAccount, userID, action.payload.clientEmail, action.payload.label);

    yield put(addClientSuccess());
    // Перезагрузить полный список, чтобы получить вычисляемые поля (brandCount, promptCount, scores)
    yield put(fetchClients());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.addClient",
      failureAction: addClientFailure,
    });
  }
}

function* detachClientSaga(action: PayloadAction<DetachClientPayload>) {
  try {
    yield put(setDetachLoading());
    const userID: string = yield* requireAccountIdOrFail();

    const data: ManagedAccount = yield call(
      apiPost<ManagedAccount>,
      agencyUrl(API_ENDPOINTS.agencyManagedAccounts, userID, `/${action.payload.managedId}/detach`),
      {
        ...AGENCY_API_OPTIONS,
        body: {
          checkout_url: `${window.location.origin}${ERoutes.Payment}`,
        },
      },
    );

    yield put(detachClientSuccess(normalize<ManagedAccount>(data)));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.detachClient",
      failureAction: detachClientFailure,
    });
  }
}

function* actAsClientSaga(action: PayloadAction<ActAsPayload>) {
  yield put(setActingAs({
    clientId: action.payload.clientId,
    clientAccountId: action.payload.clientAccountId,
    label: action.payload.label,
    email: action.payload.email,
  }));
  yield put(resetImpersonationState());
  // Navigate to overview so the user sees the client's data immediately
  window.location.href = "/overview/";
}

function* returnToAgencySaga() {
  yield put(clearActingAs());
  yield put(resetImpersonationState());
  yield put(fetchClients());
  // Hard navigate to clear any stale client-scoped data from components
  window.location.href = AGENCY_DASHBOARD_PATH;
}

function* enableAgencySaga() {
  try {
    yield put(setEnableAgencyLoading());
    yield call(ensureAgencyEnabled);
    yield put(enableAgencySuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.enableAgency",
      failureAction: enableAgencyFailure,
    });
  }
}

function* inviteClientSaga(action: PayloadAction<InviteClientPayload>) {
  try {
    yield put(setInviteLoading());
    const userID: string = yield* requireAccountIdOrFail();

    yield call(ensureAgencyEnabled);

    yield call(
      apiPost,
      agencyUrl(API_ENDPOINTS.agencyInviteClient, userID),
      {
        ...AGENCY_API_OPTIONS,
        body: {
          client_email: action.payload.clientEmail,
        },
      },
    );

    yield put(inviteClientSuccess());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.inviteClient",
      failureAction: inviteClientFailure,
    });
  }
}

function* createClientSaga(action: PayloadAction<CreateClientPayload>) {
  try {
    yield put(setCreateLoading());
    const userID: string = yield* requireAccountIdOrFail();

    yield call(ensureAgencyEnabled);

    // Backend handles: create_user + attach + welcome email atomically
    yield call(
      apiPost,
      agencyUrl(API_ENDPOINTS.agencyCreateClient, userID),
      {
        ...AGENCY_API_OPTIONS,
        body: {
          email: action.payload.email,
          password: action.payload.password,
          first_name: action.payload.firstName,
          last_name: action.payload.lastName,
          label: action.payload.label ?? null,
          login_url: `${window.location.origin}${ERoutes.Login}`,
        },
      },
    );

    yield put(createClientSuccess());
    yield put(fetchClients());
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.createClient",
      failureAction: createClientFailure,
    });
  }
}

function* renameClientSaga(action: PayloadAction<RenameClientPayload>) {
  try {
    yield put(setRenameLoading());
    const userID: string = yield* requireAccountIdOrFail();

    const data: ManagedAccount = yield call(
      apiPatch<ManagedAccount>,
      agencyUrl(API_ENDPOINTS.agencyManagedAccounts, userID, `/${action.payload.managedId}`),
      {
        ...AGENCY_API_OPTIONS,
        body: { label: action.payload.label },
      },
    );

    yield put(renameClientSuccess(normalize<ManagedAccount>(data)));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "agency.renameClient",
      failureAction: renameClientFailure,
    });
  }
}

export function* agencySaga() {
  yield takeEvery(fetchClients.type, fetchClientsSaga);
  yield takeEvery(addClient.type, addClientSaga);
  yield takeEvery(detachClient.type, detachClientSaga);
  yield takeEvery(actAsClient.type, actAsClientSaga);
  yield takeEvery(returnToAgency.type, returnToAgencySaga);
  yield takeEvery(enableAgency.type, enableAgencySaga);
  yield takeEvery(inviteClient.type, inviteClientSaga);
  yield takeEvery(createClient.type, createClientSaga);
  yield takeEvery(renameClient.type, renameClientSaga);
}

